import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import { timeout } from 'lumen/utils/waiters';

interface GeneralDropdownsMultipleSelectDropdownAjaxSignature {
  Args: {
    canSelectAll: unknown;
    isDisabled: unknown;
    isExcludeMode: unknown;
    onChange: unknown;
    onLoadMore: unknown;
    options: unknown;
    totalCount: unknown;
    translationPrefix: unknown;
    value: unknown;
    visibleItems: unknown;
  };
  Element: HTMLElement;
}

export default class GeneralDropdownsMultipleSelectDropdownAjax extends Component<GeneralDropdownsMultipleSelectDropdownAjaxSignature> {
  @service declare intl: Services['intl'];

  // pages that have been loaded into the DOM
  @tracked page = 0;
  // @ts-expect-error: Incorrect type
  @tracked searchQuery;
  @tracked tempExcludedOptionIds = new Set([]);
  @tracked tempSelectedOptionIds = new Set([]);

  loadMoreTask = task({ restartable: true }, async () => {
    await timeout();
    if (
      this.page <
      // @ts-expect-error: Incorrect type
      Math.ceil(this.args.totalCount / this.args.visibleItems) - 1
    ) {
      this.page += 1;
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.args.onLoadMore({ page: this.page, q: this.searchQuery || '' });
    }
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get options() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return this.args.options.map((option) => ({
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      id: option.id,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      label: option.name,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      unsafe_subLabel: option.description,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      badge: option.badge,
      // A bit of a hacky fix to by-pass ember-power-select's default equality check (which is ===)
      // Ember-power-select falls back on the isEqual method if it's present
      // This fixes the issue where a selected option is not highlighted in the dropdown when
      // scrolling down and loading new options
      // @see https://github.com/cibernox/ember-power-select/issues/162#issuecomment-500408532
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      isEqual(other) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return this.id === other.id;
      },
    }));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectAllLabel() {
    // As soon as the user has searched once, the select all option should be removed
    if (typeof this.searchQuery === 'string') {
      return undefined;
    }

    return this.args.canSelectAll
      ? // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        this.intl.t(`${this.args.translationPrefix}.any`, {
          // @ts-expect-error: Incorrect type
          count: this.args.totalCount,
        })
      : undefined;
  }

  @action
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  buildLabelWithCount(count, { unsafe_isExcludeMode: isExcludeMode }) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    let asyncCount = count;
    if (isExcludeMode) {
      // @ts-expect-error: Incorrect type
      asyncCount = this.args.totalCount - count;
    }

    // @ts-expect-error: Incorrect type
    return this.intl.t(this.args.translationPrefix, {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      count: asyncCount,
    });
  }

  @action
  handleClose(): void {
    // This will show the select all option and default options again when opening the dropdown
    this.searchQuery = undefined;
    this.page = 0;
    // @ts-expect-error: Incorrect type
    this.args.onLoadMore({ page: this.page, q: '' });
  }

  /**
   * Filter the possible options using the (optional) search in the dropdown list
   */
  @action
  // @ts-expect-error: Incorrect type
  handleSearch(searchText): void {
    // Set local variable used for loading more options
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.searchQuery = searchText;
    // Reset page
    this.page = 0;
    // Reset currently selected options
    // @ts-expect-error: Incorrect type
    this.args.onChange([], { isExcludeMode: false });
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.args.onLoadMore({ page: this.page, q: searchText });
  }

  <template>
    <PrismComboSelectInput
      {{! @glint-expect-error: Incorrect type }}
      @buildLabelWithCount={{this.buildLabelWithCount}}
      {{! @glint-expect-error: Incorrect type }}
      @disabled={{@isDisabled}}
      @isSearchable={{true}}
      @isSecondary={{true}}
      {{! @glint-expect-error: Incorrect type }}
      @onChange={{@onChange}}
      @onClose={{this.handleClose}}
      @onSearch={{this.handleSearch}}
      @options={{this.options}}
      @selectAllLabel={{this.selectAllLabel}}
      {{! @glint-expect-error: Incorrect type }}
      @unsafe_isExcludeMode={{@isExcludeMode}}
      @unsafe_onLoadMore={{perform this.loadMoreTask}}
      {{! @glint-expect-error: Incorrect type }}
      @value={{@value}}
      ...attributes
    />
  </template>
}
